import { Download, Filter, Hourglass, Plus, Search } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Panel } from '@/components/ui/Panel'
import { Tabs } from '@/components/ui/Tabs'
import { ListHeader } from '@/components/workbench/ListHeader'
import { glossaryLabel, navLabel, type NavItem } from '@/config/navigation'
import { useT, type TKey } from '@/i18n'
import { cn } from '@/lib/cn'

/**
 * Planned route: the workbench layout is designed, but the backend contract is
 * not deployed. No fixture data is rendered as if it were real.
 */
export function PlannedPage({ item }: { item: NavItem }) {
  const t = useT()
  const preview = item.preview!
  const label = navLabel(t, item)
  const columns = preview.columns.map((raw) => ({ label: glossaryLabel(t, raw), numeric: raw.endsWith('#') }))

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Panel className="shrink-0 overflow-visible">
        <ListHeader
          title={label}
          extra={
            <>
              <Badge tone="warning">{t('planned.apiPending')}</Badge>
              {preview.views && (
                <Tabs
                  label={t('planned.savedViews')}
                  value={preview.views[0]}
                  onChange={() => {}}
                  items={preview.views.map((view, index) => ({ value: view, label: t(`planned.views.${view}` as TKey), disabled: index > 0 }))}
                />
              )}
            </>
          }
          actions={
            <>
              {preview.kind === 'report' && (
                <Button size="sm" disabled icon={<Download className="size-4" />}>
                  {t('common.actions.export')}
                </Button>
              )}
              {preview.primaryAction && (
                <Button size="sm" variant="primary" disabled icon={<Plus className="size-4" />}>
                  {t(`planned.actions.${preview.primaryAction}` as TKey)}
                </Button>
              )}
            </>
          }
        />
      </Panel>

      <Panel className="mt-2.5 flex shrink-0 flex-wrap items-center gap-2 overflow-visible px-4 py-3" aria-disabled="true">
        <div className="w-full sm:w-72">
          <Input disabled placeholder={t('common.search')} leading={<Search className="size-3.75" />} />
        </div>
        {(preview.filters ?? []).map((filter) => (
          <span key={filter} className="inline-flex h-8 items-center gap-1.5 rounded-[calc(10px*var(--radius-scale))] border border-dashed border-line-strong px-2.5 text-[length:calc(13px*var(--text-scale))] text-subtle">
            <Filter className="size-3.5" />
            {glossaryLabel(t, filter)}
          </span>
        ))}
      </Panel>

      <Panel className="mt-2.5 flex min-h-0 flex-1 flex-col" aria-disabled="true">
        {preview.kind === 'board' ? (
          <div className="grid min-h-0 flex-1 auto-cols-[minmax(220px,1fr)] grid-flow-col gap-3 overflow-x-auto p-4">
            {columns.map((column) => (
              <div key={column.label} className="flex flex-col rounded-[calc(14px*var(--radius-scale))] border border-dashed border-line-strong p-3">
                <div className="mb-3 flex items-center justify-between text-[length:calc(13px*var(--text-scale))] font-medium text-fg-2">
                  {column.label}
                  <span className="num text-[length:calc(12px*var(--text-scale))] text-subtle">—</span>
                </div>
                {[0, 1].map((card) => (
                  <div key={card} className="mb-2 h-16 rounded-[calc(10px*var(--radius-scale))] bg-hover opacity-60" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-[length:calc(13.5px*var(--text-scale))]">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.label}
                      scope="col"
                      className={cn(
                        'h-9.5 whitespace-nowrap border-b border-line px-3 text-[length:calc(12px*var(--text-scale))] font-medium text-muted first:pl-5 last:pr-5',
                        column.numeric ? 'text-right' : 'text-left',
                      )}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody aria-hidden>
                {Array.from({ length: 5 }, (_, row) => (
                  <tr key={row} className="h-10">
                    {columns.map((column, index) => (
                      <td key={column.label} className="border-b border-line px-3 first:pl-5 last:pr-5">
                        <div
                          className={cn('h-2.5 rounded-full bg-hover', column.numeric && 'ml-auto')}
                          style={{ width: `${index === 0 ? 55 : 30 + ((row * 13 + index * 29) % 35)}%`, opacity: 1 - row * 0.16 }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col items-center gap-2 border-t border-line px-6 py-8 text-center">
          <span className="flex size-10 items-center justify-center rounded-[calc(13px*var(--radius-scale))] bg-warning-soft text-warning">
            <Hourglass className="size-4.5" strokeWidth={1.85} />
          </span>
          <p className="text-[length:calc(14px*var(--text-scale))] font-medium text-fg">{t('planned.contractPending')}</p>
          <p className="max-w-[56ch] text-[length:calc(13px*var(--text-scale))] text-muted">
            {preview.note ? t(`planned.notes.${preview.note}` as TKey) : t('planned.defaultNote', { page: label })}
          </p>
        </div>
      </Panel>
    </div>
  )
}
